import React, {useMemo, useState, useEffect} from 'react';
import {
    Bar,
    BarChart,
    CartesianGrid,
    Cell,
    Line,
    LineChart,
    Pie,
    PieChart,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis
} from 'recharts';
import {Activity, ArrowUpRight, BarChart3, Bell, ChevronDown, CreditCard, Eye, Menu, User, X, Zap} from 'lucide-react';
import PageHeader from "../components/PageHeader";
import PageFooter from "../components/PageFooter";
import AIPricingAndForecast from "../components/AIPricingAndForecast";
// import axios from 'axios';

// Sample data for charts
// const hourlyProductionData = [
//     {hour: '00:00', amount: 152},
//     {hour: '01:00', amount: 143},
//     {hour: '02:00', amount: 136},
//     {hour: '03:00', amount: 128},
//     {hour: '04:00', amount: 124},
//     {hour: '05:00', amount: 140},
//     {hour: '06:00', amount: 178},
//     {hour: '07:00', amount: 224},
//     {hour: '08:00', amount: 252},
//     {hour: '09:00', amount: 286},
//     {hour: '10:00', amount: 298},
//     {hour: '11:00', amount: 310},
//     {hour: '12:00', amount: 306},
//     {hour: '13:00', amount: 298},
//     {hour: '14:00', amount: 280},
//     {hour: '15:00', amount: 265},
//     {hour: '16:00', amount: 245},
//     {hour: '17:00', amount: 236},
//     {hour: '18:00', amount: 248},
//     {hour: '19:00', amount: 264},
//     {hour: '20:00', amount: 273},
//     {hour: '21:00', amount: 245},
//     {hour: '22:00', amount: 210},
//     {hour: '23:00', amount: 168},
// ];
//
// const dailyProductionData = [
//     {day: 'Mon', amount: 4520},
//     {day: 'Tue', amount: 4680},
//     {day: 'Wed', amount: 4890},
//     {day: 'Thu', amount: 5210},
//     {day: 'Fri', amount: 5120},
//     {day: 'Sat', amount: 4760},
//     {day: 'Sun', amount: 4350},
// ];
//
// const dailyConsumptionData = [
//     {day: 'Mon', amount: 3890},
//     {day: 'Tue', amount: 4010},
//     {day: 'Wed', amount: 4380},
//     {day: 'Thu', amount: 4640},
//     {day: 'Fri', amount: 4520},
//     {day: 'Sat', amount: 3910},
//     {day: 'Sun', amount: 3560},
// ];

// const tokenizationData = [
//     {name: 'Tokenized', value: 68},
//     {name: 'Pending', value: 32},
// ];

// const COLORS = ['#10B981', '#6366F1'];

// const COMPANIES = [
//     {id: "alpha", name: "Alpha Energy", type: "Producer", region: "TR", productionMWh: 1240, status: "Tokenized"},
//     {id: "beta", name: "Beta Power", type: "Producer", region: "EU", productionMWh: 980, status: "Tokenized"},
//     {
//         id: "hydro",
//         name: "HydroStream Power",
//         type: "Producer",
//         region: "EU",
//         productionMWh: 1125,
//         status: "In Progress"
//     },
//     {id: "wind", name: "WindForce Tech", type: "Producer", region: "TR", productionMWh: 860, status: "Tokenized"},
//     {id: "dc", name: "DataCenter Holdings", type: "Consumer", region: "TR", consumptionMWh: 1410, status: "Tokenized"},
//     {
//         id: "quantum",
//         name: "Quantum Manufacturing",
//         type: "Consumer",
//         region: "EU",
//         consumptionMWh: 980,
//         status: "Not Started"
//     },
// ];

const EnergyDashboard = () => {
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [selectedTab, setSelectedTab] = useState('production');
    const [tokenAction, setTokenAction] = useState('buy');
    const [selectedCurrency, setSelectedCurrency] = useState('USDT');
    const [tokenAmount, setTokenAmount] = useState('1000');

    const [selectedCompany, setSelectedCompany] = useState(""); // '' means "not selected"
    const [search, setSearch] = useState("");
    const [typeFilter, setTypeFilter] = useState("All"); // All | Producer | Consumer
    const [sortBy, setSortBy] = useState("Name"); // Name | Output

    const [companies, setCompanies] = useState([]);
    const [dailyProductionByCompany, setDailyProductionByCompany] = useState({});
    const [dailyConsumptionByCompany, setDailyConsumptionByCompany] = useState({});
    const [hourlyProductionByCompany, setHourlyProductionByCompany] = useState({});
    const [hourlyConsumptionByCompany, setHourlyConsumptionByCompany] = useState({});

    const [error, setError] = useState('');

    const [walletState, setWalletState] = useState({
        account: null,
        walletName: null,
        connected: false,
        provider: null,
        signer: null,
    });

    const hasCompany = Boolean(selectedCompany);

    // const companies = [
    //     {id: "c1", name: "Alpha Energy"},
    //     {id: "c2", name: "Beta Power"},
    // ];
    //
    // const dailyProductionByCompany =
    //     {
    //         c1: [{day: "Mon", amount: 12}, {day: "Tue", amount: 18}],
    //         c2:
    //             [{day: "Mon", amount: 9}, {day: "Tue", amount: 14}],
    //     };
    //
    // const dailyConsumptionByCompany =
    //     {
    //         c1: [{day: "Mon", amount: 7}, {day: "Tue", amount: 10}],
    //         c2:
    //             [{day: "Mon", amount: 11}, {day: "Tue", amount: 12}],
    //     };
    //
    // const hourlyProductionByCompany =
    //     {
    //         c1: [{hour: "00:00", amount: 0.3}, {hour: "01:00", amount: 0.4}],
    //     };
    //
    // const hourlyConsumptionByCompany =
    //     {
    //         c1: [{hour: "00:00", amount: 0.2}, {hour: "01:00", amount: 0.25}],
    //     };

    async function fetchEnergyData() {
        const res = await fetch('/api/ElectricToken/GetWholeData', {method: 'POST'});
        if (!res.ok) {
            throw new Error(`Failed to fetch: ${res.status}`);
        }
        return res.json();
    }

    useEffect(() => {
        fetchEnergyData()
            .then(data => {
                setCompanies(data.companies);
                setDailyProductionByCompany(data.dailyProductionByCompany);
                setDailyConsumptionByCompany(data.dailyConsumptionByCompany);
                setHourlyProductionByCompany(data.hourlyProductionByCompany);
                setHourlyConsumptionByCompany(data.hourlyConsumptionByCompany);
            })
            .catch(err => {
                setError(err.message || 'Error fetching data');
            });
    }, []);

// Resolve chart datasets depending on selection/tab
    const dailyData = useMemo(() => {
        if (!hasCompany) return [];
        return selectedTab === "production"
            ? (dailyProductionByCompany[selectedCompany] || [])
            : (dailyConsumptionByCompany[selectedCompany] || []);
    }, [hasCompany, selectedCompany, selectedTab, dailyProductionByCompany, dailyConsumptionByCompany]);

    const hourlyData = useMemo(() => {
        if (!hasCompany) return [];
        if (selectedTab === "production") {
            return hourlyProductionByCompany[selectedCompany] || [];
        }
        // If you don't have consumption hourly data, keep it empty or map from production as needed
        return (hourlyConsumptionByCompany[selectedCompany] || []);
    }, [hasCompany, selectedCompany, selectedTab, hourlyProductionByCompany, hourlyConsumptionByCompany]);

    const filteredCompanies = useMemo(() => {
        let list = companies.slice();

        if (typeFilter !== "All") list = list.filter((c) => c.type === typeFilter);

        if (search.trim()) {
            const q = search.trim().toLowerCase();
            list = list.filter(
                (c) => c.name.toLowerCase().includes(q) || c.region.toLowerCase().includes(q)
            );
        }

        if (sortBy === "Name") {
            list.sort((a, b) => a.name.localeCompare(b.name));
        } else {
            const out = (c) => (c.type === "Producer" ? c.productionMWh || 0 : c.consumptionMWh || 0);
            list.sort((a, b) => out(b) - out(a));
        }
        return list;
    }, [search, typeFilter, sortBy, companies]);

    const barColor = selectedTab === "production" ? "#10B981" : "#6366F1";


    // const companyData = [
    //     {
    //         id: 1,
    //         name: 'SolarPeak Energy',
    //         type: 'Producer',
    //         production: '324.5 MWh',
    //         status: 'Tokenized',
    //         progress: 100
    //     },
    //     {id: 2, name: 'WindForce Tech', type: 'Producer', production: '186.2 MWh', status: 'Tokenized', progress: 100},
    //     {
    //         id: 3,
    //         name: 'HydroStream Power',
    //         type: 'Producer',
    //         production: '215.8 MWh',
    //         status: 'In Progress',
    //         progress: 78
    //     },
    //     {
    //         id: 4,
    //         name: 'DataCenter Holdings',
    //         type: 'Consumer',
    //         consumption: '412.3 MWh',
    //         status: 'Tokenized',
    //         progress: 100
    //     },
    //     {
    //         id: 5,
    //         name: 'Quantum Manufacturing',
    //         type: 'Consumer',
    //         consumption: '256.7 MWh',
    //         status: 'Not Started',
    //         progress: 0
    //     },
    // ];

    const tokenPrices = {
        buy: {'USDT': 0.0823, 'BTC': 0.00000312, 'ETH': 0.00005104},
        sell: {'USDT': 0.0815, 'BTC': 0.00000308, 'ETH': 0.00005098}
    };

    // React.useEffect(() => {
    //     // console.log("React useEffect");
    //
    //     // sayHello();
    //
    // }, []);


    // const sayHello = async () => {
    //     var postUrl = "/api/processdatabaseselect";
    //     // var postUrl = "http://localhost:8080/api/processdatabaseselect";
    //     var postData = {
    //         key1: "value1",
    //         user: {
    //             id: 100
    //         }
    //     };
    //
    //     await fetch(postUrl, {
    //         method: "POST",
    //         headers: {
    //             Authorization: "Bearer " + localStorage.getItem("authtoken"),
    //             Accept: "application/json",
    //             "Content-Type": "application/json"
    //         },
    //         body: JSON.stringify(postData)
    //     })
    //         .then(response => response.json())
    //         .then(result => {
    //             console.log(result);
    //
    //             if (result.resultstate === 0) {
    //                 // setSwapSetups(result.swapsetups);
    //             }
    //         });
    // }

    // export async function fetchConsumptionData() {
    //     const response = await axios.get('/api/energy/consumption');
    //     return response.data;
    // }
    //
    // export async function fetchProductionData() {
    //     const response = await axios.get('/api/energy/production');
    //     return response.data;
    // }

    if (error) return <div>Error: {error}</div>;
    if (!companies.length) return <div>Loading…</div>;

    const handleLogin = () => {
        // e.g., navigate("/login") if using react-router
        window.location.href = "/login";
    };
    
    return (
        <div className="flex flex-col min-h-screen bg-gray-50 text-gray-900">

            <PageHeader
                onLogin={handleLogin}
                walletState={walletState}
                onWalletStateChange={setWalletState}
            />
            
            {/*/!* Header/Navigation Bar *!/*/}
            {/*<header className="bg-white border-b border-gray-200 sticky top-0 z-10">*/}
            {/*    <div className="px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">*/}
            {/*        <div className="flex items-center">*/}
            {/*            <div className="flex items-center gap-2">*/}
            {/*                <Zap className="h-8 w-8 text-indigo-600"/>*/}
            {/*                <span className="text-xl font-semibold">EnergyChain</span>*/}
            {/*            </div>*/}
            
            {/*            <nav className="hidden md:ml-8 md:flex md:space-x-6">*/}
            {/*                <a href="/dashboard"*/}
            {/*                   className="px-3 py-2 text-sm font-medium text-indigo-600 border-b-2 border-indigo-600">Dashboard</a>*/}
            {/*                <a href="/energy-marketplace"*/}
            {/*                   className="px-3 py-2 text-sm font-medium text-gray-500 hover:text-gray-900">Marketplace</a>*/}
            {/*                /!*<a href="/"*!/*/}
            {/*                /!*   className="px-3 py-2 text-sm font-medium text-gray-500 hover:text-gray-900">Analytics</a>*!/*/}
            {/*                /!*<a href="/"*!/*/}
            {/*                /!*   className="px-3 py-2 text-sm font-medium text-gray-500 hover:text-gray-900">Companies</a>*!/*/}
            {/*                /!*<a href="/"*!/*/}
            {/*                /!*   className="px-3 py-2 text-sm font-medium text-gray-500 hover:text-gray-900">Market</a>*!/*/}
            {/*            </nav>*/}
            {/*        </div>*/}
            
            {/*        <div className="flex items-center gap-4">*/}
            {/*            <button className="p-2 rounded-full bg-gray-100 text-gray-500 hover:bg-gray-200 hidden md:flex">*/}
            {/*                <Bell className="h-5 w-5"/>*/}
            {/*            </button>*/}
            {/*            <div className="hidden md:flex items-center gap-2 bg-gray-100 py-1 px-3 rounded-full">*/}
            {/*                <User className="h-5 w-5 text-gray-600"/>*/}
            {/*                <span className="text-sm font-medium">Admin</span>*/}
            {/*                <ChevronDown className="h-4 w-4 text-gray-500"/>*/}
            {/*            </div>*/}
            
            {/*            <button*/}
            {/*                className="md:hidden p-2"*/}
            {/*                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}*/}
            {/*            >*/}
            {/*                {mobileMenuOpen ? <X className="h-6 w-6"/> : <Menu className="h-6 w-6"/>}*/}
            {/*            </button>*/}
            {/*        </div>*/}
            {/*    </div>*/}
            
            {/*    /!* Mobile menu *!/*/}
            {/*    {mobileMenuOpen && (*/}
            {/*        <div className="md:hidden bg-white border-t border-gray-200 py-2">*/}
            {/*            <a href="/" className="block px-4 py-2 text-base font-medium text-indigo-600">Dashboard</a>*/}
            {/*            <a href="/"*/}
            {/*               className="block px-4 py-2 text-base font-medium text-gray-500 hover:text-gray-900">Exchange</a>*/}
            {/*            <a href="/"*/}
            {/*               className="block px-4 py-2 text-base font-medium text-gray-500 hover:text-gray-900">Analytics</a>*/}
            {/*            <a href="/"*/}
            {/*               className="block px-4 py-2 text-base font-medium text-gray-500 hover:text-gray-900">Companies</a>*/}
            {/*            <a href="/"*/}
            {/*               className="block px-4 py-2 text-base font-medium text-gray-500 hover:text-gray-900">Market</a>*/}
            {/*        </div>*/}
            {/*    )}*/}
            {/*</header>*/}

            {/* Main Content */}
            <main className="flex-1 px-4 sm:px-6 lg:px-8 py-6">
                {/* Dashboard Greeting */}
                <div className="mb-8">
                    <h1 className="text-2xl font-bold text-gray-900">Energy Tokenization Dashboard</h1>
                    <p className="text-gray-600 mt-1">Monitor production, consumption, and tokenization progress in
                        real-time</p>
                </div>

                {/* Quick Stats Cards */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                    <div className="bg-white rounded-xl shadow-sm p-6 flex flex-col">
                        <div className="flex items-center justify-between mb-4">
                            <div className="bg-green-100 p-2 rounded-lg">
                                <BarChart3 className="h-5 w-5 text-green-600"/>
                            </div>
                            <span className="text-xs font-medium text-gray-500 flex items-center gap-1">
                <ArrowUpRight className="h-3 w-3 text-green-500"/>
                +8.2%
              </span>
                        </div>
                        <h3 className="text-sm font-medium text-gray-500">Total Production</h3>
                        <div className="mt-1 flex items-baseline">
                            <p className="text-2xl font-semibold">34,254 MWh</p>
                            <span className="ml-2 text-sm text-gray-500">this week</span>
                        </div>
                    </div>

                    <div className="bg-white rounded-xl shadow-sm p-6 flex flex-col">
                        <div className="flex items-center justify-between mb-4">
                            <div className="bg-indigo-100 p-2 rounded-lg">
                                <Activity className="h-5 w-5 text-indigo-600"/>
                            </div>
                            <span className="text-xs font-medium text-gray-500 flex items-center gap-1">
                <ArrowUpRight className="h-3 w-3 text-green-500"/>
                +4.7%
              </span>
                        </div>
                        <h3 className="text-sm font-medium text-gray-500">Total Consumption</h3>
                        <div className="mt-1 flex items-baseline">
                            <p className="text-2xl font-semibold">28,910 MWh</p>
                            <span className="ml-2 text-sm text-gray-500">this week</span>
                        </div>
                    </div>

                    <div className="bg-white rounded-xl shadow-sm p-6 flex flex-col">
                        <div className="flex items-center justify-between mb-4">
                            <div className="bg-purple-100 p-2 rounded-lg">
                                <Zap className="h-5 w-5 text-purple-600"/>
                            </div>
                            <span className="text-xs font-medium text-gray-500 flex items-center gap-1">
                <ArrowUpRight className="h-3 w-3 text-green-500"/>
                +12.3%
              </span>
                        </div>
                        <h3 className="text-sm font-medium text-gray-500">Tokenized Energy</h3>
                        <div className="mt-1 flex items-baseline">
                            <p className="text-2xl font-semibold">23,378 MWh</p>
                            <span className="ml-2 text-sm text-gray-500">68% of total</span>
                        </div>
                    </div>

                    <div className="bg-white rounded-xl shadow-sm p-6 flex flex-col">
                        <div className="flex items-center justify-between mb-4">
                            <div className="bg-blue-100 p-2 rounded-lg">
                                <CreditCard className="h-5 w-5 text-blue-600"/>
                            </div>
                            <span className="text-xs font-medium text-gray-500 flex items-center gap-1">
                <ArrowUpRight className="h-3 w-3 text-green-500"/>
                +5.4%
              </span>
                        </div>
                        <h3 className="text-sm font-medium text-gray-500">Token Price (USDT)</h3>
                        <div className="mt-1 flex items-baseline">
                            <p className="text-2xl font-semibold">$0.0823</p>
                            <span className="ml-2 text-sm text-gray-500">per kWh</span>
                        </div>
                    </div>
                </div>

                {/* Main Dashboard Content */}
                {/*<div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">*/}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8 items-stretch min-h-0">
                    {/* Production & Consumption Charts Section */}
                    {/*    <div className="lg:col-span-2 bg-white rounded-xl shadow-sm p-6">*/}

                    <div className="lg:col-span-2 min-w-0 min-h-0">
                        <div className="bg-white rounded-xl shadow-sm p-6 h-full min-w-0 min-h-0">


                            <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-6">
                                <h2 className="text-lg font-semibold">Energy Metrics</h2>
                                <div className="mt-3 sm:mt-0">
                                    <div className="inline-flex rounded-md shadow-sm">
                                        <button
                                            type="button"
                                            onClick={() => setSelectedTab('production')}
                                            className={`px-4 py-2 text-sm font-medium rounded-l-md ${
                                                selectedTab === 'production' ? 'bg-indigo-100 text-indigo-700' : 'bg-white text-gray-700 hover:bg-gray-50'
                                            } border border-gray-200`}
                                        >
                                            Production
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => setSelectedTab('consumption')}
                                            className={`px-4 py-2 text-sm font-medium rounded-r-md ${
                                                selectedTab === 'consumption' ? 'bg-indigo-100 text-indigo-700' : 'bg-white text-gray-700 hover:bg-gray-50'
                                            } border border-gray-200 border-l-0`}
                                        >
                                            Consumption
                                        </button>
                                    </div>
                                </div>
                            </div>

                            {/* Charts */}
                            {/*<div className="mb-6">*/}
                            {/*    <h3 className="text-sm font-medium text-gray-500 mb-3">Daily Overview</h3>*/}
                            {/*    <div className="h-64">*/}
                            {/*        <ResponsiveContainer width="100%" height="100%">*/}
                            {/*            <BarChart*/}
                            {/*                data={selectedTab === 'production' ? dailyProductionData : dailyConsumptionData}*/}
                            {/*                margin={{top: 5, right: 5, bottom: 5, left: 5}}*/}
                            {/*            >*/}
                            {/*                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0"/>*/}
                            {/*                <XAxis dataKey="day" tickLine={false} axisLine={false}/>*/}
                            {/*                <YAxis tickLine={false} axisLine={false}/>*/}
                            {/*                <Tooltip*/}
                            {/*                    contentStyle={{*/}
                            {/*                        background: 'white',*/}
                            {/*                        border: 'none',*/}
                            {/*                        borderRadius: '8px',*/}
                            {/*                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)'*/}
                            {/*                    }}*/}
                            {/*                    formatter={(value) => [`${value} MWh`, 'Amount']}*/}
                            {/*                    labelStyle={{fontWeight: 'bold', marginBottom: '4px'}}*/}
                            {/*                />*/}
                            {/*                <Bar*/}
                            {/*                    dataKey="amount"*/}
                            {/*                    fill={selectedTab === 'production' ? '#10B981' : '#6366F1'}*/}
                            {/*                    radius={[4, 4, 0, 0]}*/}
                            {/*                />*/}
                            {/*            </BarChart>*/}
                            {/*        </ResponsiveContainer>*/}
                            {/*    </div>*/}
                            {/*</div>*/}

                            {/*<div>*/}
                            {/*    <h3 className="text-sm font-medium text-gray-500 mb-3">Hourly Breakdown</h3>*/}
                            {/*    <div className="h-64">*/}
                            {/*        <ResponsiveContainer width="100%" height="100%">*/}
                            {/*            <LineChart*/}
                            {/*                data={hourlyProductionData}*/}
                            {/*                margin={{top: 5, right: 5, bottom: 5, left: 5}}*/}
                            {/*            >*/}
                            {/*                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0"/>*/}
                            {/*                <XAxis dataKey="hour" tickLine={false} axisLine={false}/>*/}
                            {/*                <YAxis tickLine={false} axisLine={false}/>*/}
                            {/*                <Tooltip*/}
                            {/*                    contentStyle={{*/}
                            {/*                        background: 'white',*/}
                            {/*                        border: 'none',*/}
                            {/*                        borderRadius: '8px',*/}
                            {/*                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)'*/}
                            {/*                    }}*/}
                            {/*                    formatter={(value) => [`${value} MWh`, 'Amount']}*/}
                            {/*                    labelStyle={{fontWeight: 'bold', marginBottom: '4px'}}*/}
                            {/*                />*/}
                            {/*                <Line type="monotone" dataKey="amount"*/}
                            {/*                      stroke={selectedTab === 'production' ? '#10B981' : '#6366F1'}*/}
                            {/*                      strokeWidth={2} dot={{*/}
                            {/*                    stroke: selectedTab === 'production' ? '#10B981' : '#6366F1',*/}
                            {/*                    strokeWidth: 2,*/}
                            {/*                    r: 4*/}
                            {/*                }} activeDot={{r: 6}}/>*/}
                            {/*            </LineChart>*/}
                            {/*        </ResponsiveContainer>*/}
                            {/*    </div>*/}
                            {/*</div>*/}


                            <div className="space-y-6">
                                {/* Controls */}
                                <div className="flex flex-col md:flex-row md:items-center gap-3">
                                    <div className="flex-1">
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Select Company
                                        </label>
                                        <select
                                            className="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                            value={selectedCompany}
                                            onChange={(e) => setSelectedCompany(e.target.value)}
                                        >
                                            <option value="">— Choose a company —</option>
                                            {companies.map((c) => (
                                                <option key={c.id} value={c.id}>
                                                    {c.name}
                                                </option>
                                            ))}
                                        </select>
                                    </div>

                                    {/*<div className="inline-flex rounded-lg border border-gray-200 overflow-hidden">*/}
                                    {/*    <button*/}
                                    {/*        type="button"*/}
                                    {/*        onClick={() => setSelectedTab("production")}*/}
                                    {/*        className={`px-4 py-2 text-sm ${*/}
                                    {/*            selectedTab === "production"*/}
                                    {/*                ? "bg-gray-100 font-medium"*/}
                                    {/*                : "bg-white hover:bg-gray-50"*/}
                                    {/*        }`}*/}
                                    {/*    >*/}
                                    {/*        Production*/}
                                    {/*    </button>*/}
                                    {/*    <button*/}
                                    {/*        type="button"*/}
                                    {/*        onClick={() => setSelectedTab("consumption")}*/}
                                    {/*        className={`px-4 py-2 text-sm border-l border-gray-200 ${*/}
                                    {/*            selectedTab === "consumption"*/}
                                    {/*                ? "bg-gray-100 font-medium"*/}
                                    {/*                : "bg-white hover:bg-gray-50"*/}
                                    {/*        }`}*/}
                                    {/*    >*/}
                                    {/*        Consumption*/}
                                    {/*    </button>*/}
                                    {/*</div>*/}
                                </div>

                                {/* Daily Overview */}
                                {/*<div className="mb-6 relative">*/}
                                <div className="mb-6 relative min-h-0">
                                    {!hasCompany && (
                                        <div
                                            className="absolute inset-0 z-10 flex items-center justify-center pointer-events-none">
                                            <div className="text-sm text-gray-400 bg-white/70 px-3 py-1 rounded-md">
                                                Select a company to load data
                                            </div>
                                        </div>
                                    )}

                                    <h3 className="text-sm font-medium text-gray-500 mb-3">Daily Overview</h3>
                                    {/*<div className="h-64">*/}
                                    <div className="h-64 overflow-hidden min-h-0">
                                        <ResponsiveContainer width="100%" height="100%">
                                            <BarChart data={dailyData} margin={{top: 5, right: 5, bottom: 5, left: 5}}>
                                                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0"/>
                                                <XAxis dataKey="day" tickLine={false} axisLine={false}/>
                                                <YAxis tickLine={false} axisLine={false}/>
                                                <Tooltip
                                                    contentStyle={{
                                                        background: "white",
                                                        border: "none",
                                                        borderRadius: "8px",
                                                        boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
                                                    }}
                                                    formatter={(value) => [`${value} MWh`, "Amount"]}
                                                    labelStyle={{fontWeight: "bold", marginBottom: "4px"}}
                                                />
                                                <Bar dataKey="amount" fill={barColor} radius={[4, 4, 0, 0]}/>
                                            </BarChart>
                                        </ResponsiveContainer>
                                    </div>
                                </div>

                                {/* Hourly Breakdown */}
                                {/*<div className="relative">*/}
                                <div className="relative min-h-0">
                                    {!hasCompany && (
                                        <div
                                            className="absolute inset-0 z-10 flex items-center justify-center pointer-events-none">
                                            <div className="text-sm text-gray-400 bg-white/70 px-3 py-1 rounded-md">
                                                Select a company to load data
                                            </div>
                                        </div>
                                    )}

                                    <h3 className="text-sm font-medium text-gray-500 mb-3">Hourly Breakdown</h3>
                                    {/*<div className="h-64">*/}
                                    <div className="h-64 overflow-hidden min-h-0">
                                        <ResponsiveContainer width="100%" height="100%">
                                            <LineChart data={hourlyData}
                                                       margin={{top: 5, right: 5, bottom: 5, left: 5}}>
                                                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0"/>
                                                <XAxis dataKey="hour" tickLine={false} axisLine={false}/>
                                                <YAxis tickLine={false} axisLine={false}/>
                                                <Tooltip
                                                    contentStyle={{
                                                        background: "white",
                                                        border: "none",
                                                        borderRadius: "8px",
                                                        boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
                                                    }}
                                                    formatter={(value) => [`${value} MWh`, "Amount"]}
                                                    labelStyle={{fontWeight: "bold", marginBottom: "4px"}}
                                                />
                                                <Line
                                                    type="monotone"
                                                    dataKey="amount"
                                                    stroke={barColor}
                                                    strokeWidth={2}
                                                    dot={{stroke: barColor, strokeWidth: 2, r: 4}}
                                                    activeDot={{r: 6}}
                                                />
                                            </LineChart>
                                        </ResponsiveContainer>
                                    </div>
                                </div>
                            </div>

                            <AIPricingAndForecast
                                selectedCompany={selectedCompany}
                                companies={companies}
                                selectedTab={selectedTab}
                                accentColor={barColor}
                            />

                        </div>
                    </div>

                    {/* Tokenization and Trade Section */}
                    {/*<div className="lg:col-span-1">*/}
                    <aside className="lg:col-span-1 min-w-0 h-full">
                        {/* Tokenization Progress */}
                        {/*<div className="bg-white rounded-xl shadow-sm p-6 mb-6">*/}
                        {/*    <h2 className="text-lg font-semibold mb-4">Tokenization Progress</h2>*/}
                        {/*    <div className="h-52 mb-4">*/}
                        {/*        <ResponsiveContainer width="100%" height="100%">*/}
                        {/*            <PieChart>*/}
                        {/*                <Pie*/}
                        {/*                    data={tokenizationData}*/}
                        {/*                    cx="50%"*/}
                        {/*                    cy="50%"*/}
                        {/*                    innerRadius={60}*/}
                        {/*                    outerRadius={80}*/}
                        {/*                    paddingAngle={5}*/}
                        {/*                    dataKey="value"*/}
                        {/*                >*/}
                        {/*                    {tokenizationData.map((entry, index) => (*/}
                        {/*                        <Cell key={`cell-${index}`} fill={COLORS[index]}/>*/}
                        {/*                    ))}*/}
                        {/*                </Pie>*/}
                        {/*                <Tooltip*/}
                        {/*                    formatter={(value) => [`${value}%`, 'Percentage']}*/}
                        {/*                    contentStyle={{*/}
                        {/*                        background: 'white',*/}
                        {/*                        border: 'none',*/}
                        {/*                        borderRadius: '8px',*/}
                        {/*                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)'*/}
                        {/*                    }}*/}
                        {/*                />*/}
                        {/*            </PieChart>*/}
                        {/*        </ResponsiveContainer>*/}
                        {/*    </div>*/}
                        {/*    <div className="flex justify-between items-center">*/}
                        {/*        <div className="flex items-center gap-2">*/}
                        {/*            <div className="w-3 h-3 rounded-full bg-green-500"></div>*/}
                        {/*            <span className="text-sm">Tokenized (68%)</span>*/}
                        {/*        </div>*/}
                        {/*        <div className="flex items-center gap-2">*/}
                        {/*            <div className="w-3 h-3 rounded-full bg-indigo-500"></div>*/}
                        {/*            <span className="text-sm">Pending (32%)</span>*/}
                        {/*        </div>*/}
                        {/*    </div>*/}
                        {/*</div>*/}

                        {/* Token Trading Widget */}
                        {/*<div className="bg-white rounded-xl shadow-sm p-6">*/}
                        <div className="bg-white rounded-xl shadow-sm p-6 h-full flex flex-col">
                            <h2 className="text-lg font-semibold mb-4">Token Exchange</h2>
                            <div className="flex mb-4">
                                <button
                                    onClick={() => setTokenAction('buy')}
                                    className={`flex-1 py-2 text-sm font-medium ${
                                        tokenAction === 'buy' ? 'bg-indigo-100 text-indigo-700' : 'bg-gray-100 text-gray-700'
                                    } rounded-l-md border border-gray-200`}
                                >
                                    Buy
                                </button>
                                <button
                                    onClick={() => setTokenAction('sell')}
                                    className={`flex-1 py-2 text-sm font-medium ${
                                        tokenAction === 'sell' ? 'bg-indigo-100 text-indigo-700' : 'bg-gray-100 text-gray-700'
                                    } rounded-r-md border border-gray-200 border-l-0`}
                                >
                                    Sell
                                </button>
                            </div>

                            <div className="mb-4">
                                <label className="block text-sm font-medium text-gray-700 mb-1">Amount</label>
                                <div className="flex">
                                    <input
                                        type="text"
                                        value={tokenAmount}
                                        onChange={(e) => setTokenAmount(e.target.value)}
                                        className="flex-1 px-3 py-2 border border-gray-300 rounded-l-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                                        placeholder="0.00"
                                    />
                                    <span
                                        className="inline-flex items-center px-3 py-2 text-sm text-gray-500 bg-gray-100 border border-l-0 border-gray-300 rounded-r-md">
                                    kWh
                                  </span>
                                </div>
                            </div>

                            <div className="mb-6">
                                <label className="block text-sm font-medium text-gray-700 mb-1">Currency</label>
                                <div className="relative">
                                    <select
                                        value={selectedCurrency}
                                        onChange={(e) => setSelectedCurrency(e.target.value)}
                                        className="block w-full p-2 border border-gray-300 rounded-md appearance-none focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                                    >
                                        <option value="USDT">USDT</option>
                                        <option value="BTC">BTC</option>
                                        <option value="ETH">ETH</option>
                                    </select>
                                    <div
                                        className="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none">
                                        <ChevronDown className="h-4 w-4 text-gray-500"/>
                                    </div>
                                </div>
                            </div>

                            <div className="mb-6 p-4 bg-gray-50 rounded-md">
                                <div className="flex justify-between text-sm mb-2">
                                    <span className="text-gray-500">Rate</span>
                                    <span
                                        className="font-medium">{tokenPrices[tokenAction][selectedCurrency]} {selectedCurrency}</span>
                                </div>
                                <div className="flex justify-between text-sm mb-2">
                                    <span className="text-gray-500">Fee</span>
                                    <span className="font-medium">0.1%</span>
                                </div>
                                <div
                                    className="flex justify-between text-sm font-semibold pt-2 border-t border-gray-200">
                                    <span>Total</span>
                                    <span>
                                        {(parseFloat(tokenAmount || 0) * tokenPrices[tokenAction][selectedCurrency] * (tokenAction === 'buy' ? 1.001 : 0.999)).toFixed(6)} {selectedCurrency}
                                      </span>
                                </div>
                            </div>

                            {/*<button*/}
                            {/*    className="w-full py-3 bg-indigo-600 hover:bg-indigo-700 text-white font-medium rounded-md transition duration-150 ease-in-out">*/}
                            {/*    {tokenAction === 'buy' ? 'Buy Tokens' : 'Sell Tokens'}*/}
                            {/*</button>*/}

                            <div className="mt-auto">
                                <button
                                    className="w-full py-3 bg-indigo-600 hover:bg-indigo-700 text-white font-medium rounded-md transition duration-150 ease-in-out">
                                    {tokenAction === 'buy' ? 'Buy Tokens' : 'Sell Tokens'}
                                </button>
                            </div>
                        </div>
                        {/*</div>*/}
                    </aside>
                </div>

                {/* Companies Tokenization Table */
                }
                {/*<div className="bg-white rounded-xl shadow-sm p-6 mb-8">*/
                }
                {/*    <div className="flex justify-between items-center mb-6">*/
                }
                {/*        <h2 className="text-lg font-semibold">Companies</h2>*/
                }
                {/*        <button className="text-sm text-indigo-600 hover:text-indigo-800 font-medium">View All</button>*/
                }
                {/*    </div>*/
                }

                {/*    <div className="overflow-x-auto">*/
                }
                {/*        <table className="min-w-full">*/
                }
                {/*            <thead>*/
                }
                {/*            <tr className="border-b border-gray-200">*/
                }
                {/*                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Company</th>*/
                }
                {/*                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>*/
                }
                {/*                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Production/Consumption</th>*/
                }
                {/*                /!*<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>*!/*/
                }
                {/*                /!*<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Progress</th>*!/*/
                }
                {/*                /!*<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Action</th>*!/*/
                }
                {/*            </tr>*/
                }
                {/*            </thead>*/
                }
                {/*            <tbody className="divide-y divide-gray-200">*/
                }
                {/*            {companyData.map((company) => (*/
                }
                {/*                <tr key={company.id}>*/
                }
                {/*                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{company.name}</td>*/
                }
                {/*                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{company.type}</td>*/
                }
                {/*                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">*/
                }
                {/*                        {company.type === 'Producer' ? company.production : company.consumption}*/
                }
                {/*                    </td>*/
                }
                {/*                    /!*<td className="px-6 py-4 whitespace-nowrap">*!/*/
                }
                {/*                    /!*  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${*!/*/
                }
                {/*                    /!*      company.status === 'Tokenized' ? 'bg-green-100 text-green-800' :*!/*/
                }
                {/*                    /!*          company.status === 'In Progress' ? 'bg-yellow-100 text-yellow-800' :*!/*/
                }
                {/*                    /!*              'bg-gray-100 text-gray-800'*!/*/
                }
                {/*                    /!*  }`}>*!/*/
                }
                {/*                    /!*    {company.status}*!/*/
                }
                {/*                    /!*  </span>*!/*/
                }
                {/*                    /!*</td>*!/*/
                }
                {/*                    /!*<td className="px-6 py-4 whitespace-nowrap">*!/*/
                }
                {/*                    /!*    <div className="w-full bg-gray-200 rounded-full h-2">*!/*/
                }
                {/*                    /!*        <div*!/*/
                }
                {/*                    /!*            className={`h-2 rounded-full ${*!/*/
                }
                {/*                    /!*                company.status === 'Tokenized' ? 'bg-green-500' :*!/*/
                }
                {/*                    /!*                    company.status === 'In Progress' ? 'bg-yellow-500' :*!/*/
                }
                {/*                    /!*                        'bg-gray-300'*!/*/
                }
                {/*                    /!*            }`}*!/*/
                }
                {/*                    /!*            style={{width: `${company.progress}%`}}*!/*/
                }
                {/*                    /!*        ></div>*!/*/
                }
                {/*                    /!*    </div>*!/*/
                }
                {/*                    /!*</td>*!/*/
                }
                {/*                    /!*<td className="px-6 py-4 whitespace-nowrap text-sm font-medium">*!/*/
                }
                {/*                    /!*    {company.status === 'Not Started' ? (*!/*/
                }
                {/*                    /!*        <button className="text-indigo-600 hover:text-indigo-900">Tokenize</button>*!/*/
                }
                {/*                    /!*    ) : company.status === 'In Progress' ? (*!/*/
                }
                {/*                    /!*        <button className="text-yellow-600 hover:text-yellow-900">Continue</button>*!/*/
                }
                {/*                    /!*    ) : (*!/*/
                }
                {/*                    /!*        <button className="text-gray-400 cursor-not-allowed"*!/*/
                }
                {/*                    /!*                disabled>Completed</button>*!/*/
                }
                {/*                    /!*    )}*!/*/
                }
                {/*                    /!*</td>*!/*/
                }
                {/*                </tr>*/
                }
                {/*            ))}*/
                }
                {/*            </tbody>*/
                }
                {/*        </table>*/
                }
                {/*    </div>*/
                }
                {/*</div>*/
                }


                {/*<div className="overflow-x-auto">*/}
                {/*    <table className="min-w-full">*/}
                {/*        <thead>*/}
                {/*        <tr className="border-b border-gray-200">*/}
                {/*            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">*/}
                {/*                Company*/}
                {/*            </th>*/}
                {/*            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">*/}
                {/*                Type*/}
                {/*            </th>*/}
                {/*            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">*/}
                {/*                Region*/}
                {/*            </th>*/}
                {/*            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">*/}
                {/*                Output*/}
                {/*            </th>*/}
                {/*            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">*/}
                {/*                Action*/}
                {/*            </th>*/}
                {/*        </tr>*/}
                {/*        </thead>*/}
                {/*        <tbody className="divide-y divide-gray-200">*/}
                {/*        {filteredCompanies.map((c) => {*/}
                {/*            const out =*/}
                {/*                c.type === "Producer"*/}
                {/*                    ? `${c.productionMWh ?? 0} MWh (weekly)`*/}
                {/*                    : `${c.consumptionMWh ?? 0} MWh (weekly)`;*/}
                {/*            return (*/}
                {/*                <tr key={c.id}>*/}
                {/*                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">*/}
                {/*                        {c.name}*/}
                {/*                    </td>*/}
                {/*                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{c.type}</td>*/}
                {/*                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{c.region}</td>*/}
                {/*                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{out}</td>*/}
                {/*                    <td className="px-6 py-4 whitespace-nowrap text-right">*/}
                {/*                        <button*/}
                {/*                            onClick={() => {*/}
                {/*                                setSelectedTab(c.type === "Producer" ? "production" : "consumption");*/}
                {/*                                setSelectedCompany(c.id);*/}
                {/*                                if (typeof window !== "undefined") {*/}
                {/*                                    window.scrollTo({top: 0, behavior: "smooth"});*/}
                {/*                                }*/}
                {/*                            }}*/}
                {/*                            className="inline-flex items-center gap-1 text-indigo-600 hover:text-indigo-800 text-sm font-medium"*/}
                {/*                            title="View metrics"*/}
                {/*                        >*/}
                {/*                            <Eye className="h-4 w-4"/>*/}
                {/*                            View*/}
                {/*                        </button>*/}
                {/*                    </td>*/}
                {/*                </tr>*/}
                {/*            );*/}
                {/*        })}*/}
                {/*        {filteredCompanies.length === 0 && (*/}
                {/*            <tr>*/}
                {/*                <td*/}
                {/*                    colSpan={5}*/}
                {/*                    className="px-6 py-6 text-sm text-gray-500 text-center"*/}
                {/*                >*/}
                {/*                    No companies match the current filters.*/}
                {/*                </td>*/}
                {/*            </tr>*/}
                {/*        )}*/}
                {/*        </tbody>*/}
                {/*    </table>*/}
                {/*</div>*/}

                <div className="bg-white rounded-xl shadow-sm overflow-hidden">
                    <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                        <h2 className="text-lg font-semibold">Companies</h2>
                        <button className="text-sm text-indigo-600 hover:text-indigo-800 font-medium">
                            View All
                        </button>
                    </div>

                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Company
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Type
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Region
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Output
                                </th>
                                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Action
                                </th>
                            </tr>
                            </thead>

                            <tbody className="divide-y divide-gray-100">
                            {filteredCompanies.map((c) => {
                                const out =
                                    c.type === "Producer"
                                        ? `${c.productionMWh ?? 0} MWh (weekly)`
                                        : `${c.consumptionMWh ?? 0} MWh (weekly)`;

                                return (
                                    <tr
                                        key={c.id}
                                        className="hover:bg-gray-50 transition-colors duration-100"
                                    >
                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                            {c.name}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                <span
                    className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                        c.type === "Producer"
                            ? "bg-green-100 text-green-700"
                            : "bg-indigo-100 text-indigo-700"
                    }`}
                >
                  {c.type}
                </span>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                            {c.region}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                            {out}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-right">
                                            <button
                                                onClick={() => {
                                                    setSelectedTab(c.type === "Producer" ? "production" : "consumption");
                                                    setSelectedCompany(c.id);
                                                    if (typeof window !== "undefined") {
                                                        window.scrollTo({top: 0, behavior: "smooth"});
                                                    }
                                                }}
                                                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-sm font-medium text-indigo-600 hover:text-white hover:bg-indigo-600 transition-colors"
                                            >
                                                <Eye className="h-4 w-4"/>
                                                View
                                            </button>
                                        </td>
                                    </tr>
                                );
                            })}

                            {filteredCompanies.length === 0 && (
                                <tr>
                                    <td
                                        colSpan={5}
                                        className="px-6 py-6 text-sm text-gray-500 text-center"
                                    >
                                        No companies match the current filters.
                                    </td>
                                </tr>
                            )}
                            </tbody>
                        </table>
                    </div>
                </div>

            </main>

            <PageFooter />
        </div>
    );
};

export default EnergyDashboard;
